/* eslint-disable max-len */
import { useEffect, useRef, useState } from "react";

import { Button, CircularProgress, Grid, styled, Dialog as MuiDialog } from "@mui/material";
import { EtAuction, LandToken } from "../../types/LandToken";

import { ecoFuturesBankContractAddress, getContract, getMetaMaskSigner } from "../../services/contract.service";
import useMetaMask from "../../contexts/useMetaMask";
import { fromWei, toWei } from "web3-utils";

import useAuthorization from "../../contexts/useAuthorization";
import moment from "moment";
import { toBigInt } from "ethers";
import { UserRoles } from "../../types/User";
import { auctionCurrentPrice, sizing, sleep, toReadable } from "../../utils/methods";
import { formatValue } from "../../utils/formatValue";
import { AppRoutes } from "../../config";
import { useNavigate } from "react-router-dom";
import LandActionPopup, { PopupCTA, PopupFootnote, PopupValuePill } from "./LandActionPopup";
import { describeChainError } from "../../services/challenge.service";
import { isUserRejection } from "../../services/relay.service";

type AuctionProps = {
  token: LandToken;
  compact?: boolean;
  // `silent` skips the caller's page-level loading state. The background
  // refreshes below must pass it: LandPage unmounts its whole content subtree
  // while isLoading, which takes this component (and its ending state) with it,
  // so a loud refresh here means remount -> re-expire -> refresh, forever.
  fetchToken?(silent?: boolean): any;
  setIsAuctionLoading?: (isAuctionLoading: boolean) => void;
  auctionData?: any;
};

export type AuctionDetails = [
  currency: any,
  seller: any,
  startingPrice: any,
  endingPrice: any,
  currentPrice: any,
  highestBidder: any,
  onAuction: boolean,
  bidderSize: any,
  startingTime: any,
  duration: any
];

type BidState = "initial" | "bidding" | "processing";

// How long to keep showing "ending" while an expired auction is settled
// server-side. The two backend workers involved run every minute, so this is
// several times the expected wait — past it, something is wrong and a stale
// price box reads better than an indefinite spinner.
const ENDING_WAIT_TIMEOUT_MS = 5 * 60 * 1000;

const EFTActionButton = ({
  token,
  compact,
  fetchToken,
  setIsAuctionLoading,
  auctionData,
}: AuctionProps) => {
  // const axios = useAxios();
  const { user } = useAuthorization();
  const { library } = useMetaMask();

  const [nextBidValid, setNextBidValid] = useState(false);
  const [balance, setBalance] = useState<string>("0");
  const [nextBid, setNextBid] = useState<string>("");
  const [currentBid, setCurrentBid] = useState<string | number>("0");

  const [biddingModal, setBiddingModal] = useState<boolean>(false);
  const [confirmModal, setConfirmModal] = useState<boolean>(false);
  // Why the last cancel failed, for the confirm popup's one line of copy. setInfoMessage still
  // narrates every step but nothing paints it; without this a revert left the popup silent.
  const [cancelError, setCancelError] = useState<string>("");

  const [auctionLoading, setAuctionLoading] = useState<boolean>(true);
  const [startDate, setStartDate] = useState<number | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [startingPrice, setStartingPrice] = useState<string>("");
  const [hasBids, setHasBids] = useState<boolean>(false);
  const [auctionEnded, setAuctionEnded] = useState<boolean>(false);

  // Bids arrive oldest-first, so the last one is the standing high bid — the same derivation
  // LandPage makes for `auctionHighestBidderAddress`. Shown beside the amount in the popup.
  const highestBidderAddress: string | null = auctionData?.bids?.length
    ? auctionData.bids[auctionData.bids.length - 1]?.bidder ?? null
    : null;

  const [auctionStatus, setAuctionStatus] = useState<EtAuction["status"] | null>(null);

  const [endingInProcess, setEndingProcess] = useState<boolean>(false);
  // Latched once we stop waiting for an expired auction to settle, so clearing
  // endingInProcess doesn't just re-arm the wait and start over.
  const endingGaveUpRef = useRef(false);

  const [sellerAddress, setSellerAddress] = useState<string>("");
  // const [bidList, setBidList] = useState<any[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>("");
  // The setter still narrates each transaction step, but strict Figma leaves the popup no
  // surface to print it on (the old ProcessingText line is gone). Kept so the narration is one
  // line from being restored.
  const [, setInfoMessage] = useState<string>("");

  const [canBid, setCanBid] = useState<boolean>(false);
  const [txnLoading, setTxnLoading] = useState<boolean>(false);
  const [hasAuction, setHasAuction] = useState<boolean>(false);
  const [isTrust, setIsTrust] = useState<boolean>(false);

  const navigator = useNavigate();
  const [startedRecently, setStartedRecently] = useState(false);
  const [isExpired, setIsExpired] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [auctionStarted, setAuctionStarted] = useState(false);

  const [bidState, setBidState] = useState<BidState>("initial");

  useEffect(() => {
    if (setIsAuctionLoading) {
      if (auctionLoading || endingInProcess) {
        setIsAuctionLoading(true);
      } else {
        setIsAuctionLoading(false);
      }
    }
  }, [auctionLoading, endingInProcess]);

  // The countdown reaching zero does not end the auction: the API keeps
  // reporting `started` until a once-a-minute backend worker flips it to
  // `completed`, and a second worker then sends endAuction on-chain. Fresh
  // statuses arrive because LandPage polls while an auction is settling (see
  // its auction-settling effect) — this is only the backstop: if they never
  // arrive, drop the ending state rather than covering the price box with a
  // spinner forever, which is what used to happen on every expiry.
  useEffect(() => {
    if (!endingInProcess) {
      return undefined;
    }

    const timeout = setTimeout(() => {
      // Fall back to the last known state. The auction still ends server-side;
      // a reload picks the new status up.
      endingGaveUpRef.current = true;
      setEndingProcess(false);
    }, ENDING_WAIT_TIMEOUT_MS);

    return () => clearTimeout(timeout);
  }, [endingInProcess]);

  // Reaching a terminal status ends this auction's wait for good — any later
  // one belongs to a fresh auction, so let it use the ending flow again.
  // `completed` is deliberately not terminal here: it is part of the same wait.
  useEffect(() => {
    if (auctionStatus === "ended" || auctionStatus === "cancelled") {
      endingGaveUpRef.current = false;
    }
  }, [auctionStatus]);

  // const BidValues = [
  //   {
  //     "Mint capacity": <>{token?.etNft?.totalTreeForEasement ?
  //       formatValue(token.etNft.totalTreeForEasement, true, 3)
  //       : "N/A"}
  //       < CurrencyLabel /></>
  //   },
  //   { "Contract period": <>{token.meta?.contractPeriod} <CurrencyLabel currency={TERM_UNIT} /></> },
  //   { "Opening bid": <>{formatValue(token.meta?.startingBid)} <CurrencyLabel currency={CURRENCY_NAME} /></> },
  //   //  { "Current bid": <>{formatValue(currentBid)} <CurrencyLabel currency={CURRENCY_NAME} /></> },
  //   // { "Highest bidder": <><AddressLink sx={{ fontSize: "18px" }} type="user" address={highestBidderAddress} links={false} /></> },
  //   { "Available balance": <> {balance} <CurrencyLabel currency={CURRENCY_NAME} /></> },

  // ];

  // Check if auction has started
  useEffect(() => {
    if (startDate) {
      const now = Date.now();
      if (now >= startDate) {
        setAuctionStarted(true);
      } else {
        setAuctionStarted(false);
      }
    }
  }, [startDate]);

  useEffect(() => {
    if (compact && endDate && startDate) {
      const checkTime = () => {
        const timeLeft = (endDate as Date).getTime() - Date.now();
        const min = 60 * 1000;
        const activeTime = Date.now() - startDate;
        if (activeTime <= 2 * min) {
          if (!startedRecently) {
            setStartedRecently(true);
          }
        } else {
          if (startedRecently) {
            setStartedRecently(false);
          }
        }

        if (timeLeft <= 0 && !isExpired) {
          setIsExpired(true);
        }

        if (timeLeft <= 2 * min && !isDisabled) {
          setIsDisabled(true);
        }

        const duration = moment.duration(timeLeft, "milliseconds");
        // Only while the clock is still running: past zero the duration is
        // negative, so an unguarded `< 3` logs every second for as long as the
        // auction sits in the expired-but-not-yet-settled states.
        if (timeLeft > 0 && duration.asMinutes() < 3) {
          console.log(
            `Auction '${auctionStatus}' ending in ` +
              duration.minutes().toFixed() +
              "m " +
              duration.seconds().toFixed() +
              "s"
          );
        }
      };

      const interval = setInterval(checkTime, 1000);

      if (isExpired && auctionStatus === "started" && !endingInProcess && !endingGaveUpRef.current) {
        setEndingProcess(true);
        if (fetchToken) {
          fetchToken(true).then(() => fetchAuctionInfo());
        }
      }

      // Only `ended` is terminal — at `completed` the server has still to send
      // endAuction, so stay in the ending state and let the page's poll bring
      // the final status in (winner included, which `completed` never carries).
      if (isExpired && auctionStatus === "ended") {
        if (endingInProcess) {
          setEndingProcess(false);
        }

        clearInterval(interval);
      }

      if (auctionStatus === "cancelled") {
        if (endingInProcess) {
          setEndingProcess(false);
        }
        clearInterval(interval);
      }

      return () => clearInterval(interval);
    }
    return () => null;
  }, [endDate, isExpired, isDisabled, auctionStatus, endingInProcess]);

  useEffect(() => {
    if (compact) {
      fetchAuctionInfo();
    } else {
      fetchAuctionInfo();
    }
  }, [auctionData]);

  useEffect(() => {
    validateNextBid(nextBid);
  }, [nextBid]);

  useEffect(() => {
    switch (true) {
      case user?.role === UserRoles.TRUST:
        setErrorMessage("You cannot place bid from trust account.");
        setCanBid(false);
        setIsTrust(true);
        break;

      case sellerAddress === user?.address:
        setErrorMessage("Auction owner cannot place bid.");
        setCanBid(false);
        break;

      case token?.ownerAddress === user?.address:
        setErrorMessage("Auction owner cannot place bid.");
        setCanBid(false);
        break;

      default:
        setErrorMessage("");
        setCanBid(true);
    }
  }, [sellerAddress, user?.address, user?.role, token?.ownerAddress]);

  // useEffect(() => {
  //   fetchAuctionInfo();
  // }, [activeTab]);

  const fetchAuctionInfo = async () => {
    try {
      setAuctionLoading(true);
      const bidsRes = auctionData;
      if (!bidsRes) {
        setAuctionLoading(false);
      } else {
        const { auctionStart, startingPrice, status, winner, duration, bids } = bidsRes;
        console.log(bidsRes, auctionData, bids);
        // The opening price until the first bid, so the dialog shows it and a bid below it is
        // rejected here rather than reverting on chain (endingPrice is "0" until then).
        const currentBid = formatValue(auctionCurrentPrice(bidsRes) ?? "0", false);
        if (bids.length > 0) {
          setHasBids(true);
        }
        setStartingPrice(fromWei(startingPrice, "ether"));
        setCurrentBid(currentBid);
        setSellerAddress(token.minterAddress as string);

        const startedTs = moment(auctionStart).valueOf();
        const endTime = moment(startedTs + duration);

        setStartDate(startedTs);
        setEndDate(endTime.toDate());
        setAuctionStatus(status);

        if (status === "sold" || status === "ended" || status === "completed" || status === "cancelled") {
          setAuctionEnded(true);

          console.log("Auction status is - " + status);
          const { etNft } = token;

          const _winner = winner || etNft?.minterAddress !== etNft?.ownerAddress ? etNft?.ownerAddress : "";
          if (!winner) {
            setHasAuction(true);
            console.log("Winner is not set, using owner address", _winner);
            // `completed` is the API's "expired, endAuction not sent yet" step:
            // the on-chain auction is still open and the winner is still
            // unknown, so keep waiting instead of presenting this as a finished,
            // unsold auction. This branch used to fire cancelAuction from here
            // for the guardian — an unrequested wallet prompt that raced the
            // backend's own endAuction (which handles the no-bid case fine, see
            // EcoBank.endAuction) and would revert once that landed first.
            if (status === "completed") {
              if (!endingGaveUpRef.current) {
                setEndingProcess(true);
              }
            } else if (endingInProcess) {
              setEndingProcess(false);
              if (fetchToken) {
                await fetchToken(true);
              }
            }

            setTimeout(() => {
              setAuctionLoading(false);
            }, 1000);
            return;
          } else {
            setHasAuction(true);
            setEndingProcess(false);
            setTimeout(() => {
              setAuctionLoading(false);
            }, 1000);
          }
        } else {
          console.log("Auction status is - " + status);
          setHasAuction(true);
          setTimeout(() => {
            setAuctionLoading(false);
          }, 1000);
        }
      }
    } catch (e: any) {
      console.log({ error: e });
      setAuctionLoading(false);
    } finally {
      setAuctionLoading(false);
      if (user) {
        try {
          const signer = await getMetaMaskSigner();
          const ercContract = getContract("erc20", signer);
          if (ercContract) {
            const ercBalance = await ercContract.balanceOf(user?.address);
            setBalance(toReadable(ercBalance.toString()).toString());
          }
        } catch (error) {
          console.error("Error fetching balance:", error);
        }
      }
    }
  };

  const validateNextBid = (value: string) => {
    setNextBid(value);
    const num = Number(value);
    if (num <= 0) {
      setNextBidValid(false);
    } else if (num > 0 && value.startsWith("0") && !value.includes(".")) {
      setNextBidValid(false);
    } else if (num > Number(balance)) {
      setNextBidValid(false);
    } else if (num < Number(currentBid)) {
      setNextBidValid(false);
    } else if (num === Number(currentBid) && Number(currentBid) !== Number(startingPrice)) {
      setNextBidValid(false);
    } else if (value.includes("e") || value.includes("-")) {
      setNextBidValid(false);
    } else {
      setNextBidValid(true);
    }
  };

  const submitApproval = async (bid: BigInt | string | number) => {
    try {
      const signer = await getMetaMaskSigner();
      const erc20Contract = getContract("erc20", signer);
      if (erc20Contract) {
        setTxnLoading(true);

        // Exact-scope approval: the Bank pulls the winning bid's escrow at endAuction,
        // so the allowance only needs to cover this bid. Read the current allowance and
        // top up just the shortfall — re-bids and retries no longer stack approvals.
        const owner = await signer.getAddress();
        const currentAllowance: bigint = await erc20Contract.allowance(owner, ecoFuturesBankContractAddress);
        const required = toBigInt(bid as any);
        if (currentAllowance >= required) {
          return true;
        }

        setInfoMessage("Requesting spending approval...");
        const result = await erc20Contract.increaseAllowance(
          ecoFuturesBankContractAddress,
          required - currentAllowance
        );

        setInfoMessage("Awaiting approval request transaction...");
        const txnDone = await checkTransactionConfirmation(result.hash, true);
        if (txnDone) {
          return true;
        } else {
          throw new Error("ERC20 Approval wasn't granted. Transaction failed");
        }
      } else {
        throw new Error("ERC20 Approval wasn't granted. Contract is not initialized");
      }
    } catch (e) {
      console.log("Approval error", { error: e });
      setInfoMessage("Failed spending approval request!");
      setBidState("initial");
      return false;
    }
  };

  const submitBid = async () => {
    if (nextBidValid && Number(nextBid) > 0) {
      try {
        setBidState("processing");
        const signer = await getMetaMaskSigner();
        const auctionContract = getContract("ecofutures-bank", signer);
        if (auctionContract) {
          const bidInWei = toWei(nextBid.toString(), "ether");
          const bid = toBigInt(bidInWei);
          const approved = await submitApproval(bid);
          if (!approved) {
            throw new Error("Not approved spending");
          }

          await new Promise((resolve) => {
            setTimeout(() => {
              resolve("ok");
            }, 2000);
          });

          setInfoMessage("Requesting bidding approval...");

          setTxnLoading(true);
          console.log(bid, token.etNft?.etAuction.nftId);
          const bidResult = await auctionContract.bid(token.etNft?.etAuction.nftId, bid);

          setInfoMessage("Awaiting bidding transaction...");

          const txnRes = await checkTransactionConfirmation(bidResult.hash, false);
          if (txnRes) {
            setInfoMessage("Bid placed!");
            await new Promise((resolve) => {
              setTimeout(() => {
                resolve("ok");
              }, 2000);
            });
            // Close first, then refetch silently: the new amount flips in the price box
            // (DigitFlip), which a loud refetch would hide behind the page's re-emerge and
            // the still-open popup would cover.
            handleCloseBidding();
            if (fetchToken) {
              await fetchToken(true);
            }
            await fetchAuctionInfo();

            setTxnLoading(false);
          }
        } else {
          console.error("Contract not initialized");
        }
      } catch (e) {
        setInfoMessage("");
        setErrorMessage("Failed to submit bid. Try again later.");

        setTimeout(() => {
          setTxnLoading(false);
        }, 1000);
        console.error("Submit Bid Error", { error: e });
      }
    }
  };

  const handleBid = async (preferredBid?: string) => {
    const signer = await getMetaMaskSigner();
    const contract = getContract("erc20", signer);
    if (contract) {
      const ercBalance = await contract.balanceOf(user?.address);
      const balanceNumber = toReadable(ercBalance.toString());
      if (preferredBid) {
        setNextBid(fromWei(preferredBid, "ether"));
      }
      setBalance(balanceNumber.toString());
      setBiddingModal(true);
      setBidState("initial");
    } else {
      setErrorMessage("Failed to fetch balance from MetaMask.");
    }
  };

  const handleCancelButton = () => {
    setConfirmModal(true);
  };

  const handleCloseBidding = () => {
    setNextBid("");
    setInfoMessage("");
    setErrorMessage("");
    setBiddingModal(false);
  };

  const handleCloseConfirm = () => {
    setConfirmModal(false);
    setCancelError("");
  };

  const handleCancelAuction = async () => {
    setCancelError("");
    try {
      setInfoMessage("Requesting auction cancelling...");
      const signer = await getMetaMaskSigner();
      const contract = getContract("ecofutures-bank", signer);
      console.log("Contract", contract);

      if (contract) {
        setTxnLoading(true);

        const result = await contract.cancelAuction(token.etNft?.id);
        setInfoMessage("Waiting for transaction to complete...");

        const txnRes = await checkTransactionConfirmation(result.hash, false);
        if (txnRes) {
          setInfoMessage("Auction cancelled successfully");

          setTimeout(async () => {
            await sleep(7000);
            if (fetchToken) {
              await fetchToken();
            }
            await fetchAuctionInfo();
            setTxnLoading(false);
            setInfoMessage("");
            handleCloseConfirm();
          }, 1500);
        }
      } else {
        console.error("Contract not initialized");
      }
    } catch (e) {
      console.log({ error: e });
      setInfoMessage("Failed to cancel auction. Try again later.");
      // Names the V10.1 revert rather than showing its selector; a wallet rejection gets its
      // own line.
      setCancelError(isUserRejection(e) ? "Cancelled in your wallet — nothing was sent." : describeChainError(e));
      setTimeout(() => {
        setInfoMessage("");
        setTxnLoading(false);
      }, 2000);
    }
  };

  // const handleEndAuction = async () => {
  //   try {
  //     const signer = await getMetaMaskSigner();
  //     const ecoFuturesBankContract = getContract("ecofutures-bank", signer);
  //     console.log("End auction contract", ecoFuturesBankContract);
  //     if (ecoFuturesBankContract) {
  //       setTxnLoading(true);

  //       const result = await ecoFuturesBankContract.endAuction(token.etNft?.etAuction.nftId);
  //       const txnRes = await checkTransactionConfirmation(result.hash, false);
  //       console.log("End auction result", { result, txnRes });
  //       setTxnLoading(false);
  //       if (txnRes) {
  //         await fetchAuctionInfo();
  //         setTimeout(() => {
  //           handleCloseBidding();
  //         }, 1000);
  //       }
  //     } else {
  //       console.error("Contract not initialized");
  //     }

  //     handleCloseConfirm();
  //   } catch (e) {
  //     console.log({ error: e });
  //     setTxnLoading(false);
  //   }
  // };

  // useEffect(() => {
  //   console.log("Auction status changed:", auctionData?.status, "Winner address:", auctionData?.winner);
  //   if (auctionData?.status === "completed" && !auctionData?.winner) {
  //     console.log("Auction completed but winner address is null, handling end auction");
  //     handleEndAuction();
  //   }
  // }, [auctionData]);

  const checkTransactionConfirmation = async (hash: string, _bidding: boolean) => {
    try {
      const checkTransactionLoop = async (): Promise<boolean> => {
        try {
          const receipt = await window.ethereum.request({
            method: "eth_getTransactionReceipt",
            params: [hash],
          });

          if (receipt != null) {
            return true;
          } else {
            // Wait a bit before checking again to avoid hammering the provider
            await new Promise((resolve) => setTimeout(resolve, 1000));
            return await checkTransactionLoop();
          }
        } catch (error) {
          console.error("Error checking transaction receipt:", error);
          return false;
        }
      };

      return await checkTransactionLoop();
    } catch (e) {
      console.log({ error: e });
      return false;
    }
  };

  const handleCloseModal = () => {
    setBiddingModal(false);
    setBidState("initial");
  };

  // const handleCancelBid = () => {
  //   setBidState("initial");
  // };

  // Strict Figma: the purple panel carries only the amount field and one CTA. The bid's
  // high-water mark and the land card are supplied by LandActionPopup itself.
  const renderPopupContent = () => {
    switch (bidState) {
      case "initial":
        return (
          <>
            <PopupValuePill
              value={nextBid ? `$${Number(nextBid).toLocaleString("en-US")}` : ""}
              onChange={(e) => {
                // Strip the "$" and the thousands separators the field displays.
                const raw = e.target.value.replace(/[^0-9.]/g, "");
                validateNextBid(raw);
              }}
              variant="outlined"
              type="text"
              inputMode="decimal"
              placeholder="$0.00"
              // Only flag the field once something has been typed — an untouched empty field
              // showing a red ring on open reads as a failure the user did not cause.
              error={!!errorMessage && !!nextBid}
            />
            <PopupCTA
              onClick={submitBid}
              disabled={isDisabled || isExpired || txnLoading || !canBid || !nextBidValid}
              variant="contained"
            >
              Place bid
            </PopupCTA>
          </>
        );

      case "processing":
        return (
          <>
            <PopupValuePill
              value={nextBid ? `$${Number(nextBid).toLocaleString("en-US")}` : ""}
              variant="outlined"
              type="text"
              disabled
            />
            {/* Busy, not blocked — #333 rather than the translucent disabled fill, per the
                house popup-button spec. The spinner is the only processing signal that
                survives strict Figma. */}
            <PopupCTA
              disabled
              variant="contained"
              sx={{ "&.Mui-disabled": { backgroundColor: "#333", color: "white" } }}
            >
              <CircularProgress size={20} sx={{ color: "white" }} />
            </PopupCTA>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <>
      {!auctionEnded && canBid && !isExpired && auctionStarted && (
        <Grid container p={0} sx={{ mt: "0px", paddingTop: sizing(0) }}>
          <Grid item xs={12} className={hasAuction ? (token.etNft ? "" : "hidden") : "hidden"}>
            <Grid
              item
              xs={12}
              sx={{
                display: "flex",
                flexDirection: "column",
                direction: "ltr",
                margin: "0px",
              }}
            >
              <ActionButton
                disabled={biddingModal || isDisabled || isExpired || endingInProcess}
                onClick={() => {
                  if (user?.emailVerified && library) {
                    handleBid();
                  } else if (!user) {
                    window.dispatchEvent(new Event("openWalletDialog"));
                  } else {
                    navigator(AppRoutes.Profile.subRoutes.Identification.path);
                  }
                }}
                style={{ width: "100%", marginTop: "0px", borderRadius: "20px" }}
                sx={{
                  margin: 0,
                  marginTop: "0px",
                  width: "100%",
                  border: "none",
                  "&:hover": {
                    border: "none",
                    backgroundColor: "#333",
                    color: "white",
                  },
                  // To style the disabled state, use the MUI sx pseudo-class selector
                  "&.Mui-disabled": {
                    backgroundColor: "black",
                    color: "white",
                    border: "none",
                    cursor: "not-allowed",
                    "&:hover": {
                      border: "none",
                    },
                  },
                }}
              >
                {user?.emailVerified && library ? "Place bid" : user ? "Verify" : "Place bid"}
              </ActionButton>
            </Grid>
          </Grid>
        </Grid>
      )}
      {!isExpired && !auctionLoading && !auctionEnded && !canBid && !isTrust && auctionStarted && (
        <Grid container p={0} sx={{ mt: "auto", paddingTop: sizing(0) }}>
          <Grid item xs={12} className={hasAuction ? (token.etNft ? "" : "hidden") : "hidden"}>
            <Grid
              item
              xs={12}
              sx={{
                display: "flex",
                flexDirection: "column",
                direction: "ltr",
                margin: "0px",
              }}
            >
              <ActionButton
                disabled={confirmModal || txnLoading || isDisabled}
                color="primary"
                variant="contained"
                onClick={() => handleCancelButton()}
                style={{
                  width: "100%",
                  borderRadius: "20px",
                }}
                sx={{
                  margin: 0,
                  marginTop: "0px",
                  width: "100%",
                  border: "none",
                  "&:hover": {
                    border: "none",
                    backgroundColor: "#333",
                    color: "white",
                  },
                  // To style the disabled state, use the MUI sx pseudo-class selector
                  "&.Mui-disabled": {
                    backgroundColor: "black",
                    color: "white",
                    border: "none",
                    cursor: "not-allowed",
                    "&:hover": {
                      border: "none",
                    },
                  },
                }}
              >
                Cancel auction
              </ActionButton>
            </Grid>
          </Grid>
        </Grid>
      )}
      {(endingInProcess || auctionLoading) && (
        <Grid container p={0} sx={{ mt: "auto", paddingTop: sizing(0) }}>
          <Grid item xs={12} className={hasAuction ? (token.etNft ? "" : "hidden") : "hidden"}>
            <Grid
              item
              xs={12}
              sx={{
                display: "flex",
                flexDirection: "column",
                direction: "ltr",
                margin: "0px",
              }}
            >
              <AuctionLoading>
                <CircularProgress size={16} sx={{ color: "#fff" }} />
                {endingInProcess ? "Ending auction..." : "Loading auction..."}
              </AuctionLoading>
            </Grid>
          </Grid>
        </Grid>
      )}
      {/* Bid popup — V9 Figma (node 3001:4019). One shell for both viewports; the drawer and
          dialog copies this replaced were identical apart from padding. */}
      <LandActionPopup
        open={biddingModal}
        onClose={handleCloseModal}
        token={token}
        statLabel={hasBids ? "CURRENT BID" : "OPENING BID"}
        statValue={`$${formatValue(currentBid)}`}
        statAddress={highestBidderAddress}
      >
        {renderPopupContent()}
      </LandActionPopup>

      {/* Cancel-auction confirmation — the same shell as the bid popup above, so the seller
          sees the standing bid they are about to void in the same place the bidder saw it:
          the bid as the heading and one CTA, the shape every cancel popup keeps. No detail
          rows by design. */}
      <LandActionPopup
        open={confirmModal}
        onClose={() => {
          // A cancel in flight can't be dismissed — the wallet prompt is still up, and
          // closing here would strand the transaction narration.
          if (!txnLoading) {
            handleCloseConfirm();
          }
        }}
        token={token}
        statLabel={hasBids ? "CURRENT BID" : "OPENING BID"}
        statValue={`$${formatValue(currentBid)}`}
        statAddress={highestBidderAddress}
      >
        {/* An expired auction settles on its own (see the endingInProcess lane above); there is
            nothing to cancel, so the CTA goes and only the figure remains. */}
        {!isExpired && (
          <PopupCTA disabled={txnLoading} onClick={() => handleCancelAuction()} variant="contained">
            {txnLoading ? <CircularProgress size={20} sx={{ color: "white" }} /> : "Cancel auction"}
          </PopupCTA>
        )}
        {/* The panel has no field to carry an error ring, so the one line under the CTA is where
            a revert or a wallet rejection shows — setInfoMessage's narration has no surface. */}
        {cancelError && <PopupFootnote tone="error">{cancelError}</PopupFootnote>}
      </LandActionPopup>
    </>
  );
};

export const InfoMessage = styled("span")`
  font-size: 12px;
  margin-top: 10px;
`;

export const AuctionCard = styled("div")`
  width: 100%;
  padding: 24px;
  border-radius: 16px;
  border: var(--border);
  height: max-content;
  margin-top: 24px;
  position: relative;

  &.hidden {
    display: none;
  }

  @media (max-width: 992px) {
    margin-top: 12px;
    padding: 12px;
  }
`;

// In-flow status row sitting where the auction action button would be — NOT an
// overlay. As `position: absolute; inset: 0` it escaped to whatever positioned
// ancestor it found and blanked that out instead: the whole purple price box in
// the embedded land view, the entire two-column layout standalone.
const AuctionLoading = styled("div")`
  width: 100%;
  min-height: 36px;
  padding: 0 16px;
  display: flex;
  gap: 10px;
  justify-content: center;
  align-items: center;
  border-radius: 20px;
  background: #000;
  color: #fff;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 500;
`;

export const AuctionInput = styled("div")`
  margin-top: 12px;
  width: 100%;

  div {
    width: 100%;

    input[type="number"]::-webkit-inner-spin-button,
    input[type="number"]::-webkit-outer-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    input {
      width: 100%;
      font-family: IBM Mono, monospace;
      font-weight: 400;
      font-size: 20px;
      padding-right: 96px;
    }
  }

  fieldset {
    height: 100%;
    top: 0;
    width: 100%;
    border-radius: 8px;
    border: 1px solid ${({ theme }) => theme.palette.border.default};
  }

  height: max-content;
  margin-bottom: 12px;

  display: flex;
  flex-direction: row;
  align-items: center;

  position: relative;

  span {
    display: block;
    position: absolute;
    right: 16px;
    user-select: none;
    font-size: 16px;
    font-family: IBM Mono, monospace;
  }
`;

export const Status = styled("div")`
  width: 100%;
  display: grid;
  grid-template-columns: 50% 50%;

  .currentBid > div,
  .currentTime > div {
    display: flex;

    &:first-of-type {
      font-size: 14px;
    }
  }

  & > div:last-of-type {
    padding-left: 24px;
    border-left: var(--border);
    @media (max-width: 900px) {
      padding-left: 12px;
    }
  }

  @media (max-width: 560px) {
    grid-template-columns: 100%;
    width: 100%;
    & > div:last-of-type {
      margin-top: 12px;
      padding-top: 12px;
      padding-left: 0;
      border-top: var(--border);
      border-left: none;
    }
  }
`;

export const EasementsWrap = styled("div")`
  display: flex;
  flex-direction: column;
  align-items: start;
  width: 100%;

  background: ${({ theme }) => theme.palette.back.tiers};
  // border-bottom: 1px solid;
  // border-top: 1px solid;
  // border-color: ${({ theme }) => theme.palette.border.dark};
  color: black;
  h6 {
    font-family: inherit;
  }
`;

const ActionButton = styled(Button)`
  margin-top: 24px;
  text-transform: none;
  @media (max-width: 900px) {
    margin-top: 12px;
  }
`;

export const BasicPlan = styled("div")`
  width: 100%;
  padding-top: 24px;
  display: grid;
  grid-template-columns: 30% 70%;
  align-items: start;
  position: relative;
  border-top: 0.5px solid white;

  &.activated {
    &:after {
      content: "Activated";
      position: absolute;
      top: 5px;
      right: 10px;
      font-size: 10px;
      color: forestgreen;
    }
  }

  blockquote {
    margin: 10px 0;
  }

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
    margin-top: 12px;
    & > div:first-of-type:not(:empty) {
      padding-bottom: 12px;
      margin-bottom: 12px;
      border-bottom: 1px solid;
      border-color: ${({ theme }) => theme.palette.border.dark};
    }
  }
`;

export const EasementRight = styled("div")`
  width: 100%;
  color: black;
  font-size: 16px;
`;

export const DialogPopUp = styled(MuiDialog)`
  .MuiDialog-paper {
    max-width: 600px;
    min-width: 300px;
    background: transparent;
    border-radius: 18px;
    //box-shadow: none;
    border: 1px solid transparent;
    @media (max-width: 560px) {
      // height:350px;
      margin: 12px;
      max-width: 325px;
    }
  }
`;

export default EFTActionButton;
